/**
 * @loci/city-editions - Per-city registry for the LOCI platform.
 *
 * Two layers, and the difference matters:
 *
 * - `CITY_RECORDS` — **every** code LOCI recognises, launched or upcoming, with
 *   the facts every surface needs (display name, `Location.city`, IANA zone,
 *   and — once the market is designed — its geography: centre, bounds and
 *   default Discover anchor, COR-155).
 *   This is the single authority; `CityCode` is derived from its keys.
 * - `CITY_EDITIONS` — the codes that are actually **served**, each with a
 *   theme and copy on top of its record (and required geography). Runtime narrowing
 *   against this registry is what keeps an unlaunched code out of routing paths.
 *
 * ```ts
 * import { CITY_EDITIONS, CITY_RECORDS, getCityEdition } from '@loci/city-editions';
 *
 * CITY_RECORDS.CHI.timeZone;   // 'America/Chicago' — recognised, not yet served
 * CITY_EDITIONS.CLT.locationCity; // 'Charlotte'
 * getCityEdition('CHI');       // undefined — no edition registered
 * ```
 *
 * Or import a single city directly via its subpath:
 *
 * ```ts
 * import { CLT } from '@loci/city-editions/clt';
 * ```
 *
 * **Adding a market is one edit** (API-1704): add an entry to `CITY_RECORDS` in
 * `./cities`. Registering an edition here is the separate, later act of
 * launching it.
 *
 * @packageDocumentation
 */

import { CLT } from './clt';
import type { CityEdition } from './types';

export const CITY_EDITIONS = { CLT } as const;

/** Codes with a registered — i.e. served — {@link CityEdition}. */
export type RegisteredCityCode = keyof typeof CITY_EDITIONS;

/**
 * The registered edition for a code, or `undefined` when the city is
 * recognised-but-unlaunched (or not a city code at all).
 *
 * This is the one lookup a request handler wants: "can I serve this code, and
 * with what `Location.city` and clock?". It replaces the older two-map dance of
 * checking a code→name map, then a code→zone map, then failing loudly when they
 * disagreed — they cannot disagree now, because both facts come off one record.
 */
export const getCityEdition = (code: string): CityEdition | undefined =>
  Object.prototype.hasOwnProperty.call(CITY_EDITIONS, code)
    ? CITY_EDITIONS[code as RegisteredCityCode]
    : undefined;

export {
  CITY_RECORDS,
  CITY_RECORD_CODES_MATCH_KEYS,
  boundsAroundAnchor,
  isCityCode,
  getCityRecord,
} from './cities';
export type {
  CityBounds,
  CityDefaultAnchor,
  CityGeography,
  CityRecordCodesMatchKeys,
} from './cities';
export type {
  CityCode,
  CityCenter,
  CityTheme,
  CityCopyOverrides,
  CityEdition,
  CityRecord,
} from './types';
